import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { useIncidents } from 'context/IncidentsContext';

// ---------------------------------------------------------------------------
// SummaryWidgets — the four "district-facing" numbers on the dashboard.
//
// Every tile pulls from a real context (StudentsContext, IEPGoalsContext,
// IncidentsContext). None of the values are hardcoded. `isLive` reflects
// whether the app is talking to Supabase or falling back to the browser-only
// demo store; the badge in the header echoes the current mode so the
// teacher isn't confused about where their data lives.
// ---------------------------------------------------------------------------

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// A JS Date -> yyyy-mm-dd string. Comparing date columns as strings avoids
// timezone drift you get from doing `new Date(row.dueDate) < new Date()` on
// a date-only column (Supabase serializes those as 'YYYY-MM-DD' with no TZ).
function toIsoDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

const SummaryWidgets = () => {
  const navigate = useNavigate();
  const { students, isLive } = useStudents();
  const { goals } = useIEPGoals();
  const { incidents } = useIncidents();

  const widgets = useMemo(() => {
    const activeStudents = students.filter(
      (s) => (s.status || 'active') !== 'archived',
    ).length;

    // IEP reviews due in the next 30 days OR overdue. This is the number
    // a case manager actually cares about walking into the school year.
    const today = new Date();
    const todayIso = toIsoDate(today);
    const in30 = new Date(today.getTime() + 30 * MS_PER_DAY);
    const in30Iso = toIsoDate(in30);
    const dueSoon = students.filter((s) => {
      const due = s.iepAnnualReviewDue;
      if (!due) return false;
      // Overdue OR within the next 30 days. Archived students are excluded
      // because their IEP is inactive.
      if ((s.status || 'active') === 'archived') return false;
      return String(due) <= in30Iso;
    }).length;

    const overdueReviews = students.filter((s) => {
      const due = s.iepAnnualReviewDue;
      if (!due) return false;
      if ((s.status || 'active') === 'archived') return false;
      return String(due) < todayIso;
    }).length;

    const goalsInProgress = (Array.isArray(goals) ? goals : []).filter(
      (g) => (g.status || 'in_progress') === 'in_progress',
    ).length;

    // Behavior incidents in the last 7 days.
    const weekAgo = new Date(today.getTime() - 7 * MS_PER_DAY);
    const incidentsThisWeek = (
      Array.isArray(incidents) ? incidents : []
    ).filter((i) => {
      const at = i.occurredAt ? new Date(i.occurredAt) : null;
      if (!at || Number.isNaN(at.getTime())) return false;
      return at >= weekAgo;
    }).length;

    return [
      {
        id: 'total-students',
        title: 'Active Students',
        value: String(activeStudents),
        icon: 'Users',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        path: '/student-profile-management',
      },
      {
        id: 'reviews-due',
        title: 'IEP Reviews Due (30d)',
        value: String(dueSoon),
        icon: 'CalendarClock',
        color: overdueReviews > 0 ? 'text-red-600' : 'text-purple-600',
        bgColor: overdueReviews > 0 ? 'bg-red-50' : 'bg-purple-50',
        subtitle:
          overdueReviews > 0
            ? `${overdueReviews} overdue`
            : dueSoon > 0
            ? 'Next 30 days'
            : 'None coming up',
        path: '/student-profile-management',
      },
      {
        id: 'iep-goals',
        title: 'Goals In Progress',
        value: String(goalsInProgress),
        icon: 'Target',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        path: '/iep-goals-and-objectives-tracking',
      },
      {
        id: 'incidents',
        title: 'Incidents (7d)',
        value: String(incidentsThisWeek),
        icon: 'AlertTriangle',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        path: '/behavioral-incident-logging',
      },
    ];
  }, [students, goals, incidents]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Overview</h2>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
            isLive
              ? 'bg-green-50 text-green-700'
              : 'bg-amber-50 text-amber-700'
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              isLive ? 'bg-green-500' : 'bg-amber-500'
            }`}
          />
          {isLive ? 'Live database' : 'Demo (browser only)'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {widgets.map((widget) => (
          <button
            key={widget.id}
            type="button"
            onClick={() => navigate(widget.path)}
            className="text-left p-4 rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm cursor-pointer transition-all duration-200"
          >
            <div
              className={`w-8 h-8 ${widget.bgColor} rounded-lg flex items-center justify-center mb-3`}
            >
              <Icon name={widget.icon} size={16} className={widget.color} />
            </div>

            <div>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {widget.value}
              </p>
              <p className="text-sm text-gray-600">{widget.title}</p>
              {widget.subtitle && (
                <p className="mt-0.5 text-xs text-gray-500">
                  {widget.subtitle}
                </p>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SummaryWidgets;
